import { Notifications } from '@atlaskit/navigation-system/top-nav-items';
import { NotificationIndicator } from '@atlaskit/notification-indicator';
import { NotificationLogClient } from '@atlaskit/notification-log-client';
import Popup from '@atlaskit/popup';
import { Box, media, xcss } from '@atlaskit/primitives';
import { useTenantData } from '@dev-agents/tenant-context';
import { lazy, Suspense, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { useAuthData } from '../../../utils/auth-context';

import messages from './messages';

const NotificationsList = lazy(
	() => import(/* webpackChunkName: "NotificationList" */ '@atlassian/notification-list'),
);

const notificationLogClient = new NotificationLogClient('/gateway/api/notification-log');

const NotificationsBadge = () => {
	return (
		<NotificationIndicator
			notificationLogProvider={Promise.resolve(notificationLogClient)}
			refreshRate={60_000}
		/>
	);
};

export const NotificationsIndicator = () => {
	const { user } = useAuthData();
	const [{ cloudId }] = useTenantData().tenantContexts;
	const [isOpen, setIsOpen] = useState(false);
	const [closeCount, setCloseCount] = useState(0);
	const { formatMessage } = useIntl();

	const onClick = () => setIsOpen(() => !isOpen);
	const onClose = () => {
		setIsOpen(false);
		setCloseCount(() => closeCount + 1);
	};

	return (
		<Popup
			placement="bottom-end"
			isOpen={isOpen}
			onClose={onClose}
			trigger={(triggerProps) => (
				<Notifications
					// Re-mount when the popup is closed to force `NotificationsBadge` to fetch the new count
					key={closeCount}
					badge={NotificationsBadge}
					isSelected={isOpen}
					label={formatMessage(messages.notificationsIndicatorLabel)}
					onClick={onClick}
					testId="notifications-button"
					{...triggerProps}
				/>
			)}
			content={() => (
				<Suspense>
					<Box xcss={notificationsContentStyles}>
						<NotificationsList
							userId={user.accountId}
							cloudId={cloudId}
							testId="notification-list"
						/>
					</Box>
				</Suspense>
			)}
		/>
	);
};

const notificationsContentStyles = xcss({
	[media.above.xxs]: {
		height: '100%',
		width: '100%',
	},
	[media.above.sm]: {
		height: 'calc(100vh - 250px)',
		minHeight: '80vh',
		width: '548px',
	},
});
